import { useSelector } from "react-redux";
import { RootState } from "../../store";

/**
 * useFeatureFlag Hook
 *
 * This hook is used to check if a feature (or multiple features) is enabled in the application.
 * It abstracts Redux state access, making it easier to check feature flags
 * without manually reading from Redux in every component.
 *
 * @param featureKey - A string or an array of feature keys representing feature flags.
 * @returns {(string | boolean | undefined)[]} array of feature flag values,
 *
 * ## Example Usage:
 * ```tsx
 * const [isNewDashboardEnabled] = useFeatureFlags("newDashboard");
 * const [isNewDashboard, isDarkModeactive] = useFeatureFlags(["newDashboard", "darkMode"]);
 * ```
 */
const useFeatureFlags = (featureKey: string | string[]): (string | boolean | undefined)[] => {
    const featureFlags = useSelector(({ auth }: RootState) => auth?.configs?.env);
    const parseFeatureValue = (value: string | boolean | undefined) :
        string | boolean | undefined => {
        if (value === 'true') return true;
        if (value === 'false') return false;
        return value;
    };

    if (!featureFlags) return [];
    if (Array.isArray(featureKey)) {
        return featureKey.map((key) => parseFeatureValue(featureFlags[key]));
    }

    const featureValue = featureFlags[featureKey];
    return [parseFeatureValue(featureValue)];
};

export default useFeatureFlags;
